// Desktop-only. Do not import vw() or react-native-web here — vw() is capped at a
// 450px phone and evaluated once at module load, so it never scales on desktop.
import React, { FC, ReactNode } from "react";
import { animated, useSpring } from "@react-spring/web";
import { ReactComponent as Hotel } from "../../../assets/icons/hotel.svg";
import { ReactComponent as Restaurant } from "../../../assets/icons/restaurant.svg";
import { ReactComponent as PhoneBlack } from "../../../assets/icons/phone-filled-black.svg";
import { ReactComponent as PhoneWhite } from "../../../assets/icons/phone-filled.svg";
import { ReactComponent as LocationOutline } from "../../../assets/icons/location-outline.svg";
import { ReactComponent as Distance } from "../../../assets/icons/distance.svg";
import { ReactComponent as Global } from "../../../assets/icons/global.svg";
import { ReactComponent as Clock } from "../../../assets/icons/clock.svg";
import { ReactComponent as Arrow } from "../../../assets/icons/chevron.svg";
import { ReactComponent as ArrowTop } from "../../../assets/icons/chevron-top.svg";
import { localizeMenuName } from "../../../utils/localization/apply";
import { uiString } from "../../../utils/localization/uiStrings";
import { formatWebsiteLabel } from "../../../utils/tool/format";
import { MenuType } from "../../types/ResponseType";
import { Partner, PartnerKey, WeekDay } from "./useHeaderMenu";
import styles from "./HeaderMenuDesktop.module.scss";
import sharedStyles from "./HeaderMenu.module.scss";

const HERO_HEIGHT = 680;
const HERO_COLLAPSED_HEIGHT = 96;

const LOGO_SIZE_CLASS: Partial<Record<PartnerKey, string>> = {
  yelp: styles.logoYelp,
  doordash: styles.logoDoordash,
  openTable: styles.logoOpenTable,
};

interface HeaderMenuDesktopHeroProps {
  menu: MenuType;
  locale: string;
  videoUrl: string | null;
  status: string;
  openCloseTime: string;
  locationOpen: boolean;
  weekDaysMondayFirst: WeekDay[];
  todayKey: string;
  formatTime: (start: string, end: string, isWorkingDay: boolean | null) => string;
  activePartners: Partner[];
  isOpen: boolean;
  toggleOpen: () => void;
  switcher: ReactNode;
  handleLinkClick: (url: string | null) => void;
  handlePhoneClick: (phone: string | null) => void;
  handleMapClick: (address: string) => void;
}

export const HeaderMenuDesktopHero: FC<HeaderMenuDesktopHeroProps> = ({
  menu,
  locale,
  videoUrl,
  status,
  openCloseTime,
  locationOpen,
  weekDaysMondayFirst,
  todayKey,
  formatTime,
  activePartners,
  isOpen,
  toggleOpen,
  switcher,
  handleLinkClick,
  handlePhoneClick,
  handleMapClick,
}) => {
  const heroStyle = useSpring({
    height: isOpen ? HERO_HEIGHT : HERO_COLLAPSED_HEIGHT,
    overflow: "hidden",
  });

  return (
    <animated.div style={heroStyle} className={styles.hero}>
      {switcher && <div className={styles.languageSlot}>{switcher}</div>}

      <div
        className={`${styles.grid} ${switcher ? styles.gridWithSwitcher : ""}`}
      >
        <section className={styles.infoColumn}>
          <div className={styles.titleRow}>
            {menu?.establishmentType === "restaurant" ? (
              <Restaurant width={28} height={28} />
            ) : (
              <Hotel width={28} height={28} />
            )}
            <h1 className={styles.title}>{localizeMenuName(menu, locale)}</h1>
          </div>

          <div className={styles.statusRow}>
            <span className={locationOpen ? styles.dotOpen : styles.dotClosed} />
            <span
              className={locationOpen ? styles.statusOpen : styles.statusClosed}
            >
              {status}
            </span>
            <span className={styles.statusSep}>·</span>
            <span className={styles.statusTime}>
              <Clock width={16} height={16} />
              {openCloseTime}
            </span>
          </div>

          <aside className={styles.hoursColumn}>
            <dl className={styles.hoursList}>
              {weekDaysMondayFirst.map((day) => (
                <div
                  key={day.key}
                  className={
                    day.key === todayKey ? styles.hoursRowToday : styles.hoursRow
                  }
                >
                  <dt>{day.name}</dt>
                  <dd>{formatTime(day.start, day.end, day.isWorkingDay)}</dd>
                </div>
              ))}
            </dl>
            {activePartners.length > 0 && (
              <div className={styles.partners}>
                {activePartners.map(({ key, Logo, link }) => (
                  <div
                    key={key}
                    className={`${styles.logo} ${LOGO_SIZE_CLASS[key] ?? ""} ${
                      link ? sharedStyles.touchableOpacity : ""
                    }`}
                    onClick={() => handleLinkClick(link)}
                  >
                    <Logo />
                  </div>
                ))}
              </div>
            )}
          </aside>

          <ul className={styles.infoRows}>
            <li className={styles.infoRow}>
              <PhoneWhite width={18} height={18} />
              <span>{menu.phoneNumber}</span>
            </li>
            <li className={styles.infoRow}>
              <LocationOutline width={18} height={18} />
              <span>{menu.address}</span>
            </li>
            {menu.website && (
              <li className={styles.infoRow}>
                <Global width={18} height={18} />
                <span
                  className={styles.infoLink}
                  onClick={() => handleLinkClick(menu.website)}
                >
                  {formatWebsiteLabel(menu.website)}
                </span>
              </li>
            )}
          </ul>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.actionPrimary}
              onClick={() => handlePhoneClick(menu.phoneNumber)}
            >
              <PhoneBlack width={16} height={16} />
              {uiString(locale, "call")}
            </button>
            <button
              type="button"
              className={styles.actionSecondary}
              onClick={() => handleMapClick(menu.address)}
            >
              <Distance width={16} height={16} />
              {uiString(locale, "directions")}
            </button>
            {menu.website && (
              <button
                type="button"
                className={styles.actionSecondary}
                onClick={() => handleLinkClick(menu.website)}
              >
                <Global width={16} height={16} />
                {uiString(locale, "website")}
              </button>
            )}
          </div>
        </section>

        <div className={styles.videoColumn}>
          <div className={styles.videoFrame}>
            <video
              src={videoUrl ?? undefined}
              autoPlay
              muted
              loop
              playsInline
              className={styles.video}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>

      </div>

      {!isOpen && <div className={styles.collapseVeil} />}

      <button type="button" className={styles.toggle} onClick={toggleOpen}>
        <span>
          {isOpen
            ? uiString(locale, "hideInfo")
            : uiString(locale, "readMoreHeader")}
        </span>
        {isOpen ? (
          <ArrowTop width={16} height={16} />
        ) : (
          <Arrow width={16} height={16} />
        )}
      </button>
    </animated.div>
  );
};
